import { useState } from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Clock, Send } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "./Reveal";
import { COMPANY } from "../../data/content";

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "", privacy: false });
  const [errors, setErrors] = useState({});

  const set = (k) => (e) =>
    setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const validate = () => {
    const err = {};
    if (!form.name.trim()) err.name = "Bitte geben Sie Ihren Namen an.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) err.email = "Bitte geben Sie eine gültige E-Mail-Adresse an.";
    if (!form.message.trim()) err.message = "Bitte beschreiben Sie kurz Ihr Anliegen.";
    if (!form.privacy) err.privacy = "Bitte stimmen Sie der Datenschutzerklärung zu.";
    setErrors(err);
    return Object.keys(err).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;
    // HINWEIS: Hier kann später ein Backend- oder Formspree-Endpoint eingetragen werden,
    // z. B. fetch("https://formspree.io/f/IHRE_ID", { method: "POST", body: ... }).
    // Aktuell wird die Anfrage per mailto an info@fliesen-geffke.de übergeben.
    const body = encodeURIComponent(
      `Name: ${form.name}\nE-Mail: ${form.email}\nTelefon: ${form.phone}\n\nNachricht:\n${form.message}`
    );
    window.location.href = `mailto:${COMPANY.email}?subject=${encodeURIComponent("Anfrage über die Website")}&body=${body}`;
    toast.success("Ihr E-Mail-Programm wurde geöffnet — vielen Dank für Ihre Anfrage!");
  };

  return (
    <section id="kontakt" data-testid="contact-section" className="py-20 md:py-28 bg-[#F5F2ED]">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-5 gap-14">
        <div className="lg:col-span-2">
          <Reveal>
            <p className="text-[#B5651D] text-xs uppercase tracking-[0.25em] font-semibold mb-4">Kontakt</p>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#1F2933]">
              Kostenloses Angebot anfordern
            </h2>
            <p className="mt-4 text-[#4B5563] text-base md:text-lg">
              Erzählen Sie uns von Ihrem Projekt — wir beraten Sie fachlich
              kompetent und unverbindlich.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="mt-10 space-y-5 text-sm">
              <a href={COMPANY.phoneLink} data-testid="contact-phone-link" className="flex items-center gap-3 font-semibold text-[#1F2933] hover:text-[#B5651D] transition-colors">
                <Phone size={18} className="text-[#B5651D]" /> {COMPANY.phone}
              </a>
              <a href={`mailto:${COMPANY.email}`} data-testid="contact-email-link" className="flex items-center gap-3 font-semibold text-[#1F2933] hover:text-[#B5651D] transition-colors">
                <Mail size={18} className="text-[#B5651D]" /> {COMPANY.email}
              </a>
              <div className="flex items-start gap-3 text-[#4B5563]">
                <MapPin size={18} className="text-[#B5651D] shrink-0 mt-0.5" />
                <span>{COMPANY.street}, {COMPANY.city} ({COMPANY.district})</span>
              </div>
              <div className="flex items-start gap-3 text-[#4B5563]">
                <Clock size={18} className="text-[#B5651D] shrink-0 mt-0.5" />
                <div>
                  {COMPANY.hours.map((h) => (
                    <p key={h.days}><span className="font-semibold text-[#1F2933]">{h.days}:</span> {h.time}</p>
                  ))}
                </div>
              </div>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.15} className="lg:col-span-3">
          <form data-testid="contact-form" onSubmit={handleSubmit} noValidate className="bg-white border border-[#E5E1D8] p-8 md:p-10">
            <div className="grid sm:grid-cols-2 gap-x-8 gap-y-6">
              <div>
                <label htmlFor="name" className="text-xs uppercase tracking-wider font-semibold text-[#1F2933]">Name *</label>
                <input id="name" data-testid="contact-input-name" type="text" className="form-input" placeholder="Ihr Name" value={form.name} onChange={set("name")} />
                {errors.name && <p data-testid="error-name" className="mt-1 text-xs text-red-600">{errors.name}</p>}
              </div>
              <div>
                <label htmlFor="email" className="text-xs uppercase tracking-wider font-semibold text-[#1F2933]">E-Mail *</label>
                <input id="email" data-testid="contact-input-email" type="email" className="form-input" placeholder="ihre@email.de" value={form.email} onChange={set("email")} />
                {errors.email && <p data-testid="error-email" className="mt-1 text-xs text-red-600">{errors.email}</p>}
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="phone" className="text-xs uppercase tracking-wider font-semibold text-[#1F2933]">Telefon</label>
                <input id="phone" data-testid="contact-input-phone" type="tel" className="form-input" placeholder="Ihre Telefonnummer" value={form.phone} onChange={set("phone")} />
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="message" className="text-xs uppercase tracking-wider font-semibold text-[#1F2933]">Nachricht *</label>
                <textarea id="message" data-testid="contact-input-message" rows={4} className="form-input resize-none" placeholder="Beschreiben Sie kurz Ihr Projekt …" value={form.message} onChange={set("message")} />
                {errors.message && <p data-testid="error-message" className="mt-1 text-xs text-red-600">{errors.message}</p>}
              </div>
            </div>

            <label className="mt-6 flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                data-testid="contact-privacy-checkbox"
                checked={form.privacy}
                onChange={set("privacy")}
                className="mt-0.5 h-4 w-4 accent-[#B5651D]"
              />
              <span className="text-sm text-[#4B5563]">
                Ich habe die{" "}
                <Link to="/datenschutz" className="underline text-[#B5651D] hover:text-[#9c5517]">Datenschutzerklärung</Link>{" "}
                gelesen und stimme zu. *
              </span>
            </label>
            {errors.privacy && <p data-testid="error-privacy" className="mt-1 text-xs text-red-600">{errors.privacy}</p>}

            <button type="submit" data-testid="contact-submit-button" className="btn-primary mt-8 w-full sm:w-auto">
              Anfrage senden <Send size={16} />
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
